import React, { useRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Button,
  Box,
  MenuItem,
  CircularProgress,
  Divider,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  ToggleButton,
  Slider,
  IconButton,
  Tooltip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { useReactToPrint } from "react-to-print";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import CertificateTemplate from "./CertificateTemplate";
import SaveTemplateModal from "./SaveTemplateModal";
import DialogHeader from "app/components/common/DialogHeader";
import TextFieldLabel from "app/components/common/TextFieldLabel";
import {
  CustomSelectBox,
  CustomTextField,
  CustomTextFieldMultiline,
} from "assets/styles/globalStyledComponent";
import {
  getCertificateTemplates,
  getCertificateTemplatesList,
  upsertCertificateTemplate,
} from "app/services/schoolReportingServices/certificateTemplate.service";

// Default template configuration matching Joi schema defaults
const DEFAULT_TEMPLATE_CONFIG = {
  templateName: "Default Certificate Template",
  templateType: "Classic",
  isDefault: true,
  isActive: true,
  pageSettings: {
    orientation: "landscape",
    size: "A4",
    certificatesPerPage: 1,
    margins: {
      top: 15,
      bottom: 15,
      left: 15,
      right: 15,
    },
  },
  designSettings: {
    primaryColor: "#1e3a8a",
    secondaryColor: "#d4af37",
    accentColor: "#991b1b",
    fontFamily: "Georgia",
    borderStyle: "double",
    borderWidth: 8,
  },
  fieldsEnabled: {
    schoolLogo: true,
    schoolName: true,
    schoolAddress: true,
    certificateTitle: true,
    certificateSubtitle: true,
    studentName: true,
    courseName: true,
    academicSession: true, // Changed from completionDate
    class: true, // Changed from grade
    description: true,
    principalSignature: true,
    coordinatorSignature: true,
    date: true,
    // Removed certificateNumber
    // Removed seal
  },
  textContent: {
    certificateTitle: "Certificate of Achievement",
    certificateSubtitle: "This is to certify that",
    courseName: "Academic Excellence",
    description:
      "Has successfully completed the academic requirements and demonstrated outstanding performance",
    // Removed principalName
    // Removed coordinatorName
  },
  fieldStyles: {
    certificateTitle: {
      fontSize: 32,
      color: "#1e3a8a",
      fontWeight: "bold",
      fontStyle: "normal",
      textDecoration: "none",
      fontFamily: "Georgia",
    },
    certificateSubtitle: {
      fontSize: 18,
      color: "#4b5563",
      fontWeight: "normal",
      fontStyle: "normal",
      textDecoration: "none",
      fontFamily: "Georgia",
    },
    studentName: {
      fontSize: 36,
      color: "#1e3a8a",
      fontWeight: "bold",
      fontStyle: "italic",
      textDecoration: "none",
      fontFamily: "Georgia",
    },
    courseName: {
      fontSize: 24,
      color: "#d4af37",
      fontWeight: "600",
      fontStyle: "normal",
      textDecoration: "none",
      fontFamily: "Georgia",
    },
    description: {
      fontSize: 16,
      color: "#374151",
      fontWeight: "normal",
      fontStyle: "normal",
      textDecoration: "none",
      fontFamily: "Georgia",
    },
    // Removed principalName
    // Removed coordinatorName
  },
};

// Helper component for field styling controls - moved outside to prevent re-creation on every render
const FieldStyleControls = React.memo(
  ({
    field,
    label,
    value,
    onChange,
    style,
    checkBoxEnabled,
    handleCheckboxChange,
    handleFieldStyleChange,
    toggleTextFormat,
  }) => {
    return (
      <Box
        sx={{
          mb: 1.5,
          p: 1.5,
          backgroundColor: "#f9fafb",
          borderRadius: 1,
          border: "1px solid #e5e7eb",
        }}
      >
        <Stack spacing={1}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontSize: "12px" }}
            >
              {label}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={checkBoxEnabled[field]}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleCheckboxChange(field);
                  }}
                />
              }
              label={
                <Typography variant="caption" sx={{ fontSize: "10px" }}>
                  Show
                </Typography>
              }
              sx={{ m: 0 }}
            />
          </Box>

          {value !== undefined && (
            <CustomTextFieldMultiline
              size="small"
              fullWidth
              multiline
              rows={2}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={label}
              sx={{ backgroundColor: "#fff" }}
            />
          )}

          {/* Compact 2-column grid layout */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <Box>
              <TextFieldLabel label="Size" />
              <CustomTextField
                size="small"
                fullWidth
                type="number"
                value={style.fontSize}
                onChange={(e) =>
                  handleFieldStyleChange(field, "fontSize", e.target.value)
                }
                inputProps={{ min: 8, max: 72 }}
                sx={{ backgroundColor: "#fff" }}
              />
            </Box>

            <Box>
              <TextFieldLabel label="Weight" />
              <CustomSelectBox
                size="small"
                fullWidth
                value={style.fontWeight}
                onChange={(e) =>
                  handleFieldStyleChange(field, "fontWeight", e.target.value)
                }
                sx={{ backgroundColor: "#fff" }}
              >
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="500">Medium</MenuItem>
                {/* <MenuItem value="600">Semi Bold</MenuItem> */}
                <MenuItem value="bold">Bold</MenuItem>
              </CustomSelectBox>
            </Box>

            <Box>
              <TextFieldLabel label="Color" />
              <input
                type="color"
                value={style.color}
                onChange={(e) =>
                  handleFieldStyleChange(field, "color", e.target.value)
                }
                style={{
                  width: "100%",
                  height: "36px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  cursor: "pointer",
                  backgroundColor: "#fff",
                }}
              />
            </Box>
          </Box>

          {/* Text Formatting Buttons */}
          <Stack direction="row" spacing={0.5}>
            <ToggleButton
              value="bold"
              selected={style.fontWeight === "bold"}
              onChange={() => toggleTextFormat(field, "fontWeight")}
              size="small"
              sx={{ flex: 1, fontSize: "10px", py: 0.3, px: 0.5 }}
            >
              <FormatBoldIcon fontSize="small" sx={{ fontSize: "14px" }} />
            </ToggleButton>

            <ToggleButton
              value="italic"
              selected={style.fontStyle === "italic"}
              onChange={() => toggleTextFormat(field, "fontStyle")}
              size="small"
              sx={{ flex: 1, fontSize: "10px", py: 0.3, px: 0.5 }}
            >
              <FormatItalicIcon fontSize="small" sx={{ fontSize: "14px" }} />
            </ToggleButton>

            <ToggleButton
              value="underline"
              selected={style.textDecoration === "underline"}
              onChange={() => toggleTextFormat(field, "textDecoration")}
              size="small"
              sx={{ flex: 1, fontSize: "10px", py: 0.3, px: 0.5 }}
            >
              <FormatUnderlinedIcon
                fontSize="small"
                sx={{ fontSize: "14px" }}
              />
            </ToggleButton>
          </Stack>
        </Stack>
      </Box>
    );
  }
);

const PrintCertificateDialog = ({
  open,
  onClose,
  selectedStudents,
  schoolDetail,
  selectedSession,
}) => {
  const [loading, setLoading] = useState(false);
  const [expandedPanel, setExpandedPanel] = useState("template");

  // Zoom control state (visual only, doesn't affect print)
  const [zoomLevel, setZoomLevel] = useState(100);

  // Template management state
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [selectedTemplateName, setSelectedTemplateName] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Suppress ResizeObserver errors globally
  useEffect(() => {
    // Suppress ResizeObserver loop errors
    const suppressResizeObserver = () => {
      const resizeObserverErrDiv = document.getElementById(
        "webpack-dev-server-client-overlay"
      );
      if (resizeObserverErrDiv) {
        resizeObserverErrDiv.setAttribute("style", "display: none");
      }
    };

    const resizeObserverErrorHandler = (event) => {
      if (
        event.message ===
          "ResizeObserver loop completed with undelivered notifications." ||
        event.message === "ResizeObserver loop limit exceeded" ||
        event.message.includes("ResizeObserver")
      ) {
        const resizeObserverErrDiv = document.getElementById(
          "webpack-dev-server-client-overlay"
        );
        const resizeObserverErr = document.getElementById(
          "webpack-dev-server-client-overlay-div"
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute("style", "display: none");
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute("style", "display: none");
        }
        event.stopImmediatePropagation();
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener("error", resizeObserverErrorHandler);

    // Also suppress on unhandled rejections
    const errorHandler = (e) => {
      if (e.reason?.message?.includes("ResizeObserver")) {
        e.preventDefault();
        return false;
      }
    };

    window.addEventListener("unhandledrejection", errorHandler);

    // Suppress on interval
    const interval = setInterval(suppressResizeObserver, 100);

    return () => {
      window.removeEventListener("error", resizeObserverErrorHandler);
      window.removeEventListener("unhandledrejection", errorHandler);
      clearInterval(interval);
    };
  }, []);

  // Template & Layout Settings
  const [template, setTemplate] = useState("Classic");
  const [pageOrientation, setPageOrientation] = useState("landscape");
  const [pageSize, setPageSize] = useState("A4");
  const [certificatesPerPage, setCertificatesPerPage] = useState(1);

  // Content Fields Toggle
  const [checkBoxEnabled, setCheckBoxEnabled] = useState({
    schoolLogo: true,
    schoolName: true,
    schoolAddress: true,
    certificateTitle: true,
    certificateSubtitle: true,
    studentName: true,
    courseName: true,
    academicSession: true, // Changed from completionDate
    class: true, // Changed from grade
    description: true,
    principalSignature: true,
    coordinatorSignature: true,
    date: true,
    // Removed certificateNumber
    // Removed seal
  });

  // Field-specific styling
  const [fieldStyles, setFieldStyles] = useState({
    certificateTitle: {
      fontSize: "32",
      color: "#1e3a8a",
      fontWeight: "bold",
      fontStyle: "normal",
      textDecoration: "none",
      fontFamily: "Georgia",
    },
    certificateSubtitle: {
      fontSize: "18",
      color: "#4b5563",
      fontWeight: "normal",
      fontStyle: "normal",
      textDecoration: "none",
      fontFamily: "Georgia",
    },
    studentName: {
      fontSize: "36",
      color: "#1e3a8a",
      fontWeight: "bold",
      fontStyle: "italic",
      textDecoration: "none",
      fontFamily: "Georgia",
    },
    courseName: {
      fontSize: "24",
      color: "#d4af37",
      fontWeight: "600",
      fontStyle: "normal",
      textDecoration: "none",
      fontFamily: "Georgia",
    },
    description: {
      fontSize: "16",
      color: "#374151",
      fontWeight: "normal",
      fontStyle: "normal",
      textDecoration: "none",
      fontFamily: "Georgia",
    },
    // Removed principalName
    // Removed coordinatorName
  });

  // Customizable Text Fields
  const [certificateTitle, setCertificateTitle] = useState(
    "Certificate of Achievement"
  );
  const [certificateSubtitle, setCertificateSubtitle] = useState(
    "This is to certify that"
  );
  const [courseName, setCourseName] = useState("Academic Excellence");
  const [description, setDescription] = useState(
    "Has successfully completed the academic requirements and demonstrated outstanding performance"
  );
  // Removed principalName state
  // Removed coordinatorName state

  // Design Customization
  const [primaryColor, setPrimaryColor] = useState("#1e3a8a");
  const [secondaryColor, setSecondaryColor] = useState("#d4af37");
  const [accentColor, setAccentColor] = useState("#991b1b");
  const [fontFamily, setFontFamily] = useState("Georgia");
  const [borderStyle, setBorderStyle] = useState("double");
  const [borderWidth, setBorderWidth] = useState("8");

  // Spacing & Layout
  const [topMargin, setTopMargin] = useState("15");
  const [bottomMargin, setBottomMargin] = useState("15");
  const [leftMargin, setLeftMargin] = useState("15");
  const [rightMargin, setRightMargin] = useState("15");

  const printRef = useRef(null);
  const queryClient = useQueryClient();

  // Fetch all available templates (static + saved)
  const { data: templatesListData } = useQuery({
    queryKey: ["certificateTemplatesList", schoolDetail?._id],
    queryFn: () => getCertificateTemplatesList(),
    enabled: Boolean(schoolDetail?._id) && open,
    onSuccess: (response) => {
      const templates = response?.data?.list || [];

      // Define the 3 base templates
      const baseTemplates = [
        {
          templateName: "Classic",
          templateType: "Classic",
          isDefault: true,
        },
        {
          templateName: "Modern",
          templateType: "Modern",
          isDefault: true,
        },
        {
          templateName: "Elegant",
          templateType: "Elegant",
          isDefault: true,
        },
      ];

      // Get saved template names
      const savedTemplateNames = templates.map((t) => t.templateName);

      // Only include base templates that don't exist in saved templates
      const filteredBaseTemplates = baseTemplates.filter(
        (baseTemplate) =>
          !savedTemplateNames.includes(baseTemplate.templateName)
      );

      // Combine filtered base templates with saved templates
      const allTemplates = [...filteredBaseTemplates, ...templates];
      setAvailableTemplates(allTemplates);

      // Set initial template if not already set
      if (!selectedTemplateName && allTemplates.length > 0) {
        const classicTemplate = allTemplates.find(
          (t) => t.templateType === "Classic"
        );
        setSelectedTemplateName(
          classicTemplate?.templateName || allTemplates[0].templateName
        );
      }
    },
  });

  // Fetch specific template data when selected
  useQuery({
    queryKey: ["certificateTemplate", schoolDetail?._id, selectedTemplateName],
    queryFn: () => {
      // Find the selected template
      const selectedTemplate = availableTemplates.find(
        (t) => t.templateName === selectedTemplateName
      );

      // If it's a default template, fetch by templateType
      if (selectedTemplate?.isDefault) {
        return getCertificateTemplates({
          templateType: selectedTemplate.templateType,
        });
      }

      // If it's a saved template, fetch by templateName
      return getCertificateTemplates({ templateName: selectedTemplateName });
    },
    enabled:
      Boolean(schoolDetail?._id) &&
      Boolean(selectedTemplateName) &&
      open &&
      availableTemplates.length > 0,
    onSuccess: (response) => {
      const fetchedTemplate = response?.data?.obj || response?.data?.list?.[0];

      if (fetchedTemplate) {
        loadTemplateSettings(fetchedTemplate);
      } else {
        // If no saved data, load defaults for the template type
        const selectedTemplate = availableTemplates.find(
          (t) => t.templateName === selectedTemplateName
        );
        if (selectedTemplate) {
          loadTemplateSettings({
            ...DEFAULT_TEMPLATE_CONFIG,
            templateType: selectedTemplate.templateType,
          });
        }
      }
    },
    onError: (error) => {
      loadTemplateSettings(DEFAULT_TEMPLATE_CONFIG);
    },
  });

  // Save template mutation (update existing)
  const saveTemplateMutation = useMutation({
    mutationFn: (payload) => upsertCertificateTemplate(payload),
    onSuccess: () => {
      toast.success("Certificate template saved successfully!");
      queryClient.invalidateQueries([
        "certificateTemplatesList",
        schoolDetail?._id,
      ]);
      queryClient.invalidateQueries([
        "certificateTemplate",
        schoolDetail?._id,
        selectedTemplateName,
      ]);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to save template");
    },
  });

  // Save as new template mutation
  const saveAsNewMutation = useMutation({
    mutationFn: (payload) => upsertCertificateTemplate(payload),
    onSuccess: (response) => {
      const newTemplate = response?.data?.obj;
      toast.success("New template saved successfully!");

      // Refresh template list
      queryClient.invalidateQueries([
        "certificateTemplatesList",
        schoolDetail?._id,
      ]);

      // Select the newly created template
      if (newTemplate?.templateName) {
        setSelectedTemplateName(newTemplate.templateName);
      }

      setShowSaveModal(false);
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || "Failed to save new template"
      );
    },
  });

  // Load template settings from backend data
  const loadTemplateSettings = (templateData) => {
    // Page Settings
    if (templateData.pageSettings) {
      setPageOrientation(
        templateData.pageSettings.orientation ||
          DEFAULT_TEMPLATE_CONFIG.pageSettings.orientation
      );
      setPageSize(
        templateData.pageSettings.size ||
          DEFAULT_TEMPLATE_CONFIG.pageSettings.size
      );
      setCertificatesPerPage(
        templateData.pageSettings.certificatesPerPage ||
          DEFAULT_TEMPLATE_CONFIG.pageSettings.certificatesPerPage
      );

      if (templateData.pageSettings.margins) {
        setTopMargin(
          String(
            templateData.pageSettings.margins.top ??
              DEFAULT_TEMPLATE_CONFIG.pageSettings.margins.top
          )
        );
        setBottomMargin(
          String(
            templateData.pageSettings.margins.bottom ??
              DEFAULT_TEMPLATE_CONFIG.pageSettings.margins.bottom
          )
        );
        setLeftMargin(
          String(
            templateData.pageSettings.margins.left ??
              DEFAULT_TEMPLATE_CONFIG.pageSettings.margins.left
          )
        );
        setRightMargin(
          String(
            templateData.pageSettings.margins.right ??
              DEFAULT_TEMPLATE_CONFIG.pageSettings.margins.right
          )
        );
      }
    } else {
      // Reset to defaults if no page settings
      setPageOrientation(DEFAULT_TEMPLATE_CONFIG.pageSettings.orientation);
      setPageSize(DEFAULT_TEMPLATE_CONFIG.pageSettings.size);
      setCertificatesPerPage(
        DEFAULT_TEMPLATE_CONFIG.pageSettings.certificatesPerPage
      );
      setTopMargin(String(DEFAULT_TEMPLATE_CONFIG.pageSettings.margins.top));
      setBottomMargin(
        String(DEFAULT_TEMPLATE_CONFIG.pageSettings.margins.bottom)
      );
      setLeftMargin(String(DEFAULT_TEMPLATE_CONFIG.pageSettings.margins.left));
      setRightMargin(
        String(DEFAULT_TEMPLATE_CONFIG.pageSettings.margins.right)
      );
    }

    // Design Settings
    if (templateData.designSettings) {
      setPrimaryColor(
        templateData.designSettings.primaryColor ||
          DEFAULT_TEMPLATE_CONFIG.designSettings.primaryColor
      );
      setSecondaryColor(
        templateData.designSettings.secondaryColor ||
          DEFAULT_TEMPLATE_CONFIG.designSettings.secondaryColor
      );
      setAccentColor(
        templateData.designSettings.accentColor ||
          DEFAULT_TEMPLATE_CONFIG.designSettings.accentColor
      );
      setFontFamily(
        templateData.designSettings.fontFamily ||
          DEFAULT_TEMPLATE_CONFIG.designSettings.fontFamily
      );
      setBorderStyle(
        templateData.designSettings.borderStyle ||
          DEFAULT_TEMPLATE_CONFIG.designSettings.borderStyle
      );
      setBorderWidth(
        String(
          templateData.designSettings.borderWidth ??
            DEFAULT_TEMPLATE_CONFIG.designSettings.borderWidth
        )
      );
    } else {
      // Reset to defaults if no design settings
      setPrimaryColor(DEFAULT_TEMPLATE_CONFIG.designSettings.primaryColor);
      setSecondaryColor(DEFAULT_TEMPLATE_CONFIG.designSettings.secondaryColor);
      setAccentColor(DEFAULT_TEMPLATE_CONFIG.designSettings.accentColor);
      setFontFamily(DEFAULT_TEMPLATE_CONFIG.designSettings.fontFamily);
      setBorderStyle(DEFAULT_TEMPLATE_CONFIG.designSettings.borderStyle);
      setBorderWidth(
        String(DEFAULT_TEMPLATE_CONFIG.designSettings.borderWidth)
      );
    }

    // Set the template type to render the correct component (Classic, Modern, or Elegant)
    if (templateData.templateType) {
      setTemplate(templateData.templateType);
    }

    // Fields Enabled - align with backend response
    if (templateData.fieldsEnabled) {
      setCheckBoxEnabled({
        schoolLogo:
          templateData.fieldsEnabled.schoolLogo ??
          DEFAULT_TEMPLATE_CONFIG.fieldsEnabled.schoolLogo,
        schoolName:
          templateData.fieldsEnabled.schoolName ??
          DEFAULT_TEMPLATE_CONFIG.fieldsEnabled.schoolName,
        schoolAddress:
          templateData.fieldsEnabled.schoolAddress ??
          DEFAULT_TEMPLATE_CONFIG.fieldsEnabled.schoolAddress,
        certificateTitle:
          templateData.fieldsEnabled.certificateTitle ??
          DEFAULT_TEMPLATE_CONFIG.fieldsEnabled.certificateTitle,
        certificateSubtitle:
          templateData.fieldsEnabled.certificateSubtitle ??
          DEFAULT_TEMPLATE_CONFIG.fieldsEnabled.certificateSubtitle,
        studentName:
          templateData.fieldsEnabled.studentName ??
          DEFAULT_TEMPLATE_CONFIG.fieldsEnabled.studentName,
        courseName:
          templateData.fieldsEnabled.courseName ??
          DEFAULT_TEMPLATE_CONFIG.fieldsEnabled.courseName,
        academicSession:
          templateData.fieldsEnabled.academicSession ??
          templateData.fieldsEnabled.completionDate ??
          DEFAULT_TEMPLATE_CONFIG.fieldsEnabled.academicSession,
        class:
          templateData.fieldsEnabled.class ??
          templateData.fieldsEnabled.grade ??
          DEFAULT_TEMPLATE_CONFIG.fieldsEnabled.class,
        description:
          templateData.fieldsEnabled.description ??
          DEFAULT_TEMPLATE_CONFIG.fieldsEnabled.description,
        principalSignature:
          templateData.fieldsEnabled.principalSignature ??
          DEFAULT_TEMPLATE_CONFIG.fieldsEnabled.principalSignature,
        coordinatorSignature:
          templateData.fieldsEnabled.coordinatorSignature ??
          DEFAULT_TEMPLATE_CONFIG.fieldsEnabled.coordinatorSignature,
        date:
          templateData.fieldsEnabled.date ??
          DEFAULT_TEMPLATE_CONFIG.fieldsEnabled.date,
        // Removed certificateNumber
        // Removed seal
      });
    } else {
      // Reset to defaults if no fields enabled
      setCheckBoxEnabled(DEFAULT_TEMPLATE_CONFIG.fieldsEnabled);
    }

    // Text Content
    if (templateData.textContent) {
      setCertificateTitle(
        templateData.textContent.certificateTitle ||
          DEFAULT_TEMPLATE_CONFIG.textContent.certificateTitle
      );
      setCertificateSubtitle(
        templateData.textContent.certificateSubtitle ||
          DEFAULT_TEMPLATE_CONFIG.textContent.certificateSubtitle
      );
      setCourseName(
        templateData.textContent.courseName ||
          DEFAULT_TEMPLATE_CONFIG.textContent.courseName
      );
      setDescription(
        templateData.textContent.description ||
          DEFAULT_TEMPLATE_CONFIG.textContent.description
      );
      // Removed setPrincipalName
      // Removed setCoordinatorName
    } else {
      // Reset to defaults if no text content
      setCertificateTitle(DEFAULT_TEMPLATE_CONFIG.textContent.certificateTitle);
      setCertificateSubtitle(
        DEFAULT_TEMPLATE_CONFIG.textContent.certificateSubtitle
      );
      setCourseName(DEFAULT_TEMPLATE_CONFIG.textContent.courseName);
      setDescription(DEFAULT_TEMPLATE_CONFIG.textContent.description);
      // Removed setPrincipalName
      // Removed setCoordinatorName
    }

    // Field Styles
    if (templateData.fieldStyles) {
      const loadedStyles = {};
      Object.keys(DEFAULT_TEMPLATE_CONFIG.fieldStyles).forEach((field) => {
        loadedStyles[field] = {
          ...DEFAULT_TEMPLATE_CONFIG.fieldStyles[field],
          ...(templateData.fieldStyles[field] || {}),
          // Ensure fontSize is string for inputs
          fontSize: String(
            templateData.fieldStyles[field]?.fontSize ??
              DEFAULT_TEMPLATE_CONFIG.fieldStyles[field].fontSize
          ),
        };
      });
      setFieldStyles(loadedStyles);
    } else {
      // Reset to defaults if no field styles
      const defaultStyles = {};
      Object.keys(DEFAULT_TEMPLATE_CONFIG.fieldStyles).forEach((field) => {
        defaultStyles[field] = {
          ...DEFAULT_TEMPLATE_CONFIG.fieldStyles[field],
          fontSize: String(DEFAULT_TEMPLATE_CONFIG.fieldStyles[field].fontSize),
        };
      });
      setFieldStyles(defaultStyles);
    }
  };

  // Handle save template
  const handleSaveTemplate = () => {
    const payload = {
      templateName: selectedTemplateName, // Use the selected template name
      templateType: template, // This ensures each template type has unique settings
      isDefault: false,
      isActive: true,
      pageSettings: {
        orientation: pageOrientation,
        size: pageSize,
        certificatesPerPage: certificatesPerPage,
        margins: {
          top: Number(topMargin),
          bottom: Number(bottomMargin),
          left: Number(leftMargin),
          right: Number(rightMargin),
        },
      },
      designSettings: {
        primaryColor,
        secondaryColor,
        accentColor,
        fontFamily,
        borderStyle,
        borderWidth: Number(borderWidth),
      },
      fieldsEnabled: checkBoxEnabled,
      textContent: {
        certificateTitle,
        certificateSubtitle,
        courseName,
        description,
        // Removed principalName
        // Removed coordinatorName
      },
      fieldStyles: {
        certificateTitle: {
          ...fieldStyles.certificateTitle,
          fontSize: Number(fieldStyles.certificateTitle.fontSize),
        },
        certificateSubtitle: {
          ...fieldStyles.certificateSubtitle,
          fontSize: Number(fieldStyles.certificateSubtitle.fontSize),
        },
        studentName: {
          ...fieldStyles.studentName,
          fontSize: Number(fieldStyles.studentName.fontSize),
        },
        courseName: {
          ...fieldStyles.courseName,
          fontSize: Number(fieldStyles.courseName.fontSize),
        },
        description: {
          ...fieldStyles.description,
          fontSize: Number(fieldStyles.description.fontSize),
        },
        // Removed principalName
        // Removed coordinatorName
      },
    };

    saveTemplateMutation.mutate(payload);
  };

  // Handle reset to default values
  const handleResetTemplate = () => {
    loadTemplateSettings(DEFAULT_TEMPLATE_CONFIG);
    toast.info("Template reset to default values");
  };

  // Handle accordion panel change with debounce to prevent ResizeObserver errors
  const handlePanelChange = (panel) => (event, isExpanded) => {
    // Use requestAnimationFrame to defer state update and prevent ResizeObserver loop
    requestAnimationFrame(() => {
      setExpandedPanel(isExpanded ? panel : false);
    });
  };

  // Handle checkbox toggle
  const handleCheckboxChange = (field) => {
    setCheckBoxEnabled((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Handle field style change
  const handleFieldStyleChange = (field, property, value) => {
    setFieldStyles((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [property]: value,
      },
    }));
  };

  // Toggle text decoration (bold, italic, underline)
  const toggleTextFormat = (field, format) => {
    setFieldStyles((prev) => {
      const currentValue = prev[field][format];
      let newValue;

      if (format === "fontWeight") {
        newValue = currentValue === "bold" ? "normal" : "bold";
      } else if (format === "fontStyle") {
        newValue = currentValue === "italic" ? "normal" : "italic";
      } else if (format === "textDecoration") {
        newValue = currentValue === "underline" ? "none" : "underline";
      }

      return {
        ...prev,
        [field]: {
          ...prev[field],
          [format]: newValue,
        },
      };
    });
  };

  // Handle template selection change
  const handleTemplateChange = (templateName) => {
    setSelectedTemplateName(templateName);

    // Update the template type based on selection
    const selectedTemplate = availableTemplates.find(
      (t) => t.templateName === templateName
    );
    if (selectedTemplate) {
      setTemplate(selectedTemplate.templateType);
    }
  };

  // Handle save as new template
  const handleSaveAsNew = (newTemplateName) => {
    const selectedTemplate = availableTemplates.find(
      (t) => t.templateName === selectedTemplateName
    );

    const payload = {
      templateName: newTemplateName,
      templateType: selectedTemplate?.templateType || template,
      isDefault: false,
      isActive: true,
      pageSettings: {
        orientation: pageOrientation,
        size: pageSize,
        certificatesPerPage: certificatesPerPage,
        margins: {
          top: Number(topMargin),
          bottom: Number(bottomMargin),
          left: Number(leftMargin),
          right: Number(rightMargin),
        },
      },
      designSettings: {
        primaryColor,
        secondaryColor,
        accentColor,
        fontFamily,
        borderStyle,
        borderWidth: Number(borderWidth),
      },
      fieldsEnabled: checkBoxEnabled,
      textContent: {
        certificateTitle,
        certificateSubtitle,
        courseName,
        description,
      },
      fieldStyles: {
        certificateTitle: {
          ...fieldStyles.certificateTitle,
          fontSize: Number(fieldStyles.certificateTitle.fontSize),
        },
        certificateSubtitle: {
          ...fieldStyles.certificateSubtitle,
          fontSize: Number(fieldStyles.certificateSubtitle.fontSize),
        },
        studentName: {
          ...fieldStyles.studentName,
          fontSize: Number(fieldStyles.studentName.fontSize),
        },
        courseName: {
          ...fieldStyles.courseName,
          fontSize: Number(fieldStyles.courseName.fontSize),
        },
        description: {
          ...fieldStyles.description,
          fontSize: Number(fieldStyles.description.fontSize),
        },
      },
    };

    saveAsNewMutation.mutate(payload);
  };

  // Handle zoom level change
  const handleZoomChange = (event, newValue) => {
    setZoomLevel(newValue);
  };

  // Handle mouse wheel zoom with Ctrl key
  useEffect(() => {
    const handleWheel = (e) => {
      if (e.ctrlKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -5 : 5;
        setZoomLevel((prev) => Math.min(Math.max(prev + delta, 50), 200));
      }
    };

    const previewContainer = document.getElementById(
      "certificate-preview-container"
    );
    if (previewContainer) {
      previewContainer.addEventListener("wheel", handleWheel, {
        passive: false,
      });
    }

    return () => {
      if (previewContainer) {
        previewContainer.removeEventListener("wheel", handleWheel);
      }
    };
  }, []);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Certificates_${selectedSession || "Session"}`,
    pageStyle: `
      @page {
        size: ${pageSize} ${pageOrientation};
        margin: ${topMargin}mm ${rightMargin}mm ${bottomMargin}mm ${leftMargin}mm;
      }

      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          height: 100%;
          width: 100%;
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
          print-color-adjust: exact !important;
          background: white !important;
        }

        * {
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .loading-overlay {
          display: none !important;
        }

        [data-print-container] {
          border: none !important;
          background: white !important;
          box-shadow: none !important;
          padding: 0 !important;
          margin: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
          transform: none !important;
          transform-origin: initial !important;
        }

      
          .certificate-item {
            page-break-after: always;
            page-break-inside: avoid;
          }
          .certificate-item:last-child {
            page-break-after: auto;
          }
      }
    `,
  });

  const handlePrintClick = () => {
    if (!printRef.current) {
      console.error("Print ref is null!");
      return;
    }

    if (selectedStudents?.length === 0) {
      console.error("No students selected!");
      return;
    }

    try {
      setLoading(true);
      setTimeout(() => {
        handlePrint();
        setLoading(false);
      }, 300);
    } catch (error) {
      console.error("Print click error:", error);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setLoading(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          minWidth: "95vw",
          maxWidth: "95vw",
          maxHeight: "95vh",
          height: "95vh",
        },
      }}
      maxWidth={false}
    >
      <DialogTitle sx={{ p: "0 !important" }}>
        <DialogHeader
          title="Print Certificates - Live Preview & Customization"
          handleClose={handleClose}
        />
      </DialogTitle>

      <DialogContent
        sx={{ p: 0, overflow: "hidden", display: "flex", height: "100%" }}
      >
        {/* Left Side - Customization Panel with Accordions */}
        <Box
          sx={{
            width: "420px",
            borderRight: "1px solid #e0e0e0",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "auto",
            backgroundColor: "#fafafa",
          }}
        >
          {/* Template & Layout Accordion */}
          <Accordion
            expanded={expandedPanel === "template"}
            onChange={handlePanelChange("template")}
            disableGutters
            elevation={0}
            square
            TransitionProps={{
              timeout: 200,
              unmountOnExit: false,
              mountOnEnter: false,
            }}
            sx={{
              borderBottom: "1px solid #e0e0e0",
              "&:before": { display: "none" },
              transition: "margin 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: "#fff",
                minHeight: "48px !important",
                borderBottom:
                  expandedPanel === "template" ? "1px solid #f0f0f0" : "none",
                "&.Mui-expanded": {
                  minHeight: "48px !important",
                  margin: 0,
                },
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>
                Template & Layout
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Box onClick={(e) => e.stopPropagation()}>
                  <TextFieldLabel title="Template Design" />
                  <CustomSelectBox
                    size="small"
                    fullWidth
                    value={selectedTemplateName || ""}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleTemplateChange(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    MenuProps={{
                      onClick: (e) => e.stopPropagation(),
                    }}
                  >
                    {/* Default Templates Section */}
                    {availableTemplates.some((t) => t.isDefault) && (
                      <MenuItem
                        disabled
                        sx={{
                          backgroundColor: "#f5f5f5 !important",
                          fontWeight: 600,
                          color: "#666 !important",
                          cursor: "default !important",
                        }}
                      >
                        Default
                      </MenuItem>
                    )}
                    {availableTemplates
                      .filter((tmpl) => tmpl.isDefault)
                      .map((tmpl) => (
                        <MenuItem
                          key={tmpl.templateName}
                          value={tmpl.templateName}
                          sx={{ pl: 3 }}
                        >
                          {tmpl.templateName}
                        </MenuItem>
                      ))}

                    {/* Saved Templates Section */}
                    {availableTemplates.some((t) => !t.isDefault) && (
                      <MenuItem
                        disabled
                        sx={{
                          backgroundColor: "#f5f5f5 !important",
                          fontWeight: 600,
                          color: "#666 !important",
                          cursor: "default !important",
                          mt: 1,
                        }}
                      >
                        Saved
                      </MenuItem>
                    )}
                    {availableTemplates
                      .filter((tmpl) => !tmpl.isDefault)
                      .map((tmpl) => (
                        <MenuItem
                          key={tmpl.templateName}
                          value={tmpl.templateName}
                          sx={{ pl: 3 }}
                        >
                          {tmpl.templateName}
                        </MenuItem>
                      ))}
                  </CustomSelectBox>
                </Box>

                <Box onClick={(e) => e.stopPropagation()}>
                  <TextFieldLabel title="Page Orientation" />
                  <CustomSelectBox
                    size="small"
                    fullWidth
                    value={pageOrientation}
                    onChange={(e) => {
                      e.stopPropagation();
                      setPageOrientation(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    MenuProps={{
                      onClick: (e) => e.stopPropagation(),
                    }}
                  >
                    <MenuItem value="landscape">Landscape</MenuItem>
                    <MenuItem value="portrait">Portrait</MenuItem>
                  </CustomSelectBox>
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Content Fields Accordion */}
          <Accordion
            expanded={expandedPanel === "content"}
            onChange={handlePanelChange("content")}
            disableGutters
            elevation={0}
            square
            TransitionProps={{
              timeout: 200,
              unmountOnExit: false,
              mountOnEnter: false,
            }}
            sx={{
              borderBottom: "1px solid #e0e0e0",
              "&:before": { display: "none" },
              transition: "margin 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: "#fff",
                minHeight: "48px !important",
                borderBottom:
                  expandedPanel === "content" ? "1px solid #f0f0f0" : "none",
                "&.Mui-expanded": {
                  minHeight: "48px !important",
                  margin: 0,
                },
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>
                Content & Text Fields
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Stack spacing={1}>
                <FieldStyleControls
                  field="certificateTitle"
                  label="Certificate Title"
                  value={certificateTitle}
                  onChange={setCertificateTitle}
                  style={fieldStyles.certificateTitle}
                  checkBoxEnabled={checkBoxEnabled}
                  handleCheckboxChange={handleCheckboxChange}
                  handleFieldStyleChange={handleFieldStyleChange}
                  toggleTextFormat={toggleTextFormat}
                />

                <FieldStyleControls
                  field="certificateSubtitle"
                  label="Certificate Subtitle"
                  value={certificateSubtitle}
                  onChange={setCertificateSubtitle}
                  style={fieldStyles.certificateSubtitle}
                  checkBoxEnabled={checkBoxEnabled}
                  handleCheckboxChange={handleCheckboxChange}
                  handleFieldStyleChange={handleFieldStyleChange}
                  toggleTextFormat={toggleTextFormat}
                />

                <FieldStyleControls
                  field="studentName"
                  label="Student Name Style"
                  style={fieldStyles.studentName}
                  checkBoxEnabled={checkBoxEnabled}
                  handleCheckboxChange={handleCheckboxChange}
                  handleFieldStyleChange={handleFieldStyleChange}
                  toggleTextFormat={toggleTextFormat}
                />

                <FieldStyleControls
                  field="courseName"
                  label="Course Name"
                  value={courseName}
                  onChange={setCourseName}
                  style={fieldStyles.courseName}
                  checkBoxEnabled={checkBoxEnabled}
                  handleCheckboxChange={handleCheckboxChange}
                  handleFieldStyleChange={handleFieldStyleChange}
                  toggleTextFormat={toggleTextFormat}
                />

                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    backgroundColor: "#f9fafb",
                    borderRadius: 1,
                    border: "1px solid #e5e7eb",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Stack spacing={1.5}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, fontSize: "13px" }}
                      >
                        Description Text
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={checkBoxEnabled.description}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleCheckboxChange("description");
                            }}
                          />
                        }
                        label={
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "11px" }}
                          >
                            Show
                          </Typography>
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Box>

                    <CustomTextFieldMultiline
                      size="small"
                      fullWidth
                      multiline
                      rows={3}
                      value={description}
                      onChange={(e) => {
                        e.stopPropagation();
                        setDescription(e.target.value);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ backgroundColor: "#fff" }}
                    />

                    <Stack direction="row" spacing={1}>
                      <Box sx={{ flex: 1 }}>
                        <TextFieldLabel label="Font Size" />
                        <CustomTextField
                          size="small"
                          fullWidth
                          type="number"
                          value={fieldStyles.description.fontSize}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleFieldStyleChange(
                              "description",
                              "fontSize",
                              e.target.value
                            );
                          }}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ backgroundColor: "#fff" }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <TextFieldLabel label="Color" />
                        <input
                          type="color"
                          value={fieldStyles.description.color}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleFieldStyleChange(
                              "description",
                              "color",
                              e.target.value
                            );
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: "100%",
                            height: "38px",
                            border: "1px solid #d1d5db",
                            borderRadius: "4px",
                            cursor: "pointer",
                            backgroundColor: "#fff",
                          }}
                        />
                      </Box>
                    </Stack>
                  </Stack>
                </Box>

                {/* <FieldStyleControls
                  field="principalName"
                  label="Principal Name"
                  value={principalName}
                  onChange={setPrincipalName}
                  style={fieldStyles.principalName}
                  checkBoxEnabled={checkBoxEnabled}
                  handleCheckboxChange={handleCheckboxChange}
                  handleFieldStyleChange={handleFieldStyleChange}
                  toggleTextFormat={toggleTextFormat}
                />

                <FieldStyleControls
                  field="coordinatorName"
                  label="Coordinator Name"
                  value={coordinatorName}
                  onChange={setCoordinatorName}
                  style={fieldStyles.coordinatorName}
                  checkBoxEnabled={checkBoxEnabled}
                  handleCheckboxChange={handleCheckboxChange}
                  handleFieldStyleChange={handleFieldStyleChange}
                  toggleTextFormat={toggleTextFormat}
                /> */}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Design & Colors Accordion */}
          <Accordion
            expanded={expandedPanel === "design"}
            onChange={handlePanelChange("design")}
            disableGutters
            elevation={0}
            square
            TransitionProps={{
              timeout: 200,
              unmountOnExit: false,
              mountOnEnter: false,
            }}
            sx={{
              borderBottom: "1px solid #e0e0e0",
              "&:before": { display: "none" },
              transition: "margin 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: "#fff",
                minHeight: "48px !important",
                borderBottom:
                  expandedPanel === "design" ? "1px solid #f0f0f0" : "none",
                "&.Mui-expanded": {
                  minHeight: "48px !important",
                  margin: 0,
                },
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>
                Design & Border Colors
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Stack spacing={2}>
                {/* Color Pickers */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    justifyContent: "space-between",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Box sx={{ flex: "1 1 150px", minWidth: 150 }}>
                    <TextFieldLabel title="Primary" />
                    <input
                      type="color"
                      value={primaryColor}
                      onChange={(e) => {
                        e.stopPropagation();
                        setPrimaryColor(e.target.value);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: "100%",
                        height: "40px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: "1 1 150px", minWidth: 150 }}>
                    <TextFieldLabel title="Secondary" />
                    <input
                      type="color"
                      value={secondaryColor}
                      onChange={(e) => {
                        e.stopPropagation();
                        setSecondaryColor(e.target.value);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: "100%",
                        height: "40px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: "1 1 150px", minWidth: 150 }}>
                    <TextFieldLabel title="Accent" />
                    <input
                      type="color"
                      value={accentColor}
                      onChange={(e) => {
                        e.stopPropagation();
                        setAccentColor(e.target.value);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: "100%",
                        height: "40px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 1 }} />

                {/* Font and Border Settings */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    justifyContent: "space-between",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Box sx={{ flex: "1 1 200px", minWidth: 180 }}>
                    <TextFieldLabel title="Default Font Family" />
                    <CustomSelectBox
                      size="small"
                      fullWidth
                      value={fontFamily}
                      onChange={(e) => {
                        e.stopPropagation();
                        setFontFamily(e.target.value);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      MenuProps={{
                        onClick: (e) => e.stopPropagation(),
                      }}
                    >
                      <MenuItem value="Georgia">Georgia</MenuItem>
                      <MenuItem value="Times New Roman">
                        Times New Roman
                      </MenuItem>
                      <MenuItem value="Arial">Arial</MenuItem>
                      <MenuItem value="Courier New">Courier New</MenuItem>
                      <MenuItem value="Verdana">Verdana</MenuItem>
                      <MenuItem value="Garamond">Garamond</MenuItem>
                    </CustomSelectBox>
                  </Box>

                  <Box sx={{ flex: "1 1 180px", minWidth: 150 }}>
                    <TextFieldLabel title="Border Style" />
                    <CustomSelectBox
                      size="small"
                      fullWidth
                      value={borderStyle}
                      onChange={(e) => {
                        e.stopPropagation();
                        setBorderStyle(e.target.value);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      MenuProps={{
                        onClick: (e) => e.stopPropagation(),
                      }}
                    >
                      <MenuItem value="none">No Border</MenuItem>
                      <MenuItem value="solid">Solid</MenuItem>
                      <MenuItem value="double">Double</MenuItem>
                      <MenuItem value="dashed">Dashed</MenuItem>
                      <MenuItem value="dotted">Dotted</MenuItem>
                      <MenuItem value="ridge">Ridge</MenuItem>
                      <MenuItem value="groove">Groove</MenuItem>
                    </CustomSelectBox>
                  </Box>

                  <Box sx={{ flex: "1 1 150px", minWidth: 120 }}>
                    <TextFieldLabel title="Border Width (px)" />
                    <CustomTextField
                      size="small"
                      fullWidth
                      type="number"
                      value={borderWidth}
                      onChange={(e) => {
                        e.stopPropagation();
                        setBorderWidth(e.target.value);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      inputProps={{ min: 1, max: 20 }}
                    />
                  </Box>
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Other Options Accordion */}
          <Accordion
            expanded={expandedPanel === "other"}
            onChange={handlePanelChange("other")}
            disableGutters
            elevation={0}
            square
            TransitionProps={{
              timeout: 200,
              unmountOnExit: false,
              mountOnEnter: false,
            }}
            sx={{
              borderBottom: "1px solid #e0e0e0",
              "&:before": { display: "none" },
              transition: "margin 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: "#fff",
                minHeight: "48px !important",
                borderBottom:
                  expandedPanel === "other" ? "1px solid #f0f0f0" : "none",
                "&.Mui-expanded": {
                  minHeight: "48px !important",
                  margin: 0,
                },
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>
                Other Field Options
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Stack spacing={1} onClick={(e) => e.stopPropagation()}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={checkBoxEnabled.schoolLogo}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange("schoolLogo");
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2">Show School Logo</Typography>
                  }
                  onClick={(e) => e.stopPropagation()}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={checkBoxEnabled.schoolName}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange("schoolName");
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2">Show School Name</Typography>
                  }
                  onClick={(e) => e.stopPropagation()}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={checkBoxEnabled.schoolAddress}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange("schoolAddress");
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2">Show School Address</Typography>
                  }
                  onClick={(e) => e.stopPropagation()}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={checkBoxEnabled.academicSession}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange("academicSession");
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Show Academic Session
                    </Typography>
                  }
                  onClick={(e) => e.stopPropagation()}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={checkBoxEnabled.class}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange("class");
                      }}
                    />
                  }
                  label={<Typography variant="body2">Show Class</Typography>}
                  onClick={(e) => e.stopPropagation()}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={checkBoxEnabled.principalSignature}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange("principalSignature");
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Show Principal Signature
                    </Typography>
                  }
                  onClick={(e) => e.stopPropagation()}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={checkBoxEnabled.coordinatorSignature}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange("coordinatorSignature");
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Show Coordinator Signature
                    </Typography>
                  }
                  onClick={(e) => e.stopPropagation()}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={checkBoxEnabled.date}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange("date");
                      }}
                    />
                  }
                  label={<Typography variant="body2">Show Date</Typography>}
                  onClick={(e) => e.stopPropagation()}
                />
                {/* <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={checkBoxEnabled.certificateNumber}
                      onChange={() => handleCheckboxChange("certificateNumber")}
                    />
                  }
                  label={<Typography variant="body2">Show Certificate Number</Typography>}
                /> */}
                {/* <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={checkBoxEnabled.seal}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange("seal");
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2">Show Official Seal</Typography>
                  }
                  onClick={(e) => e.stopPropagation()}
                /> */}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Right Side - Live Preview */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            backgroundColor: "#f5f5f5",
          }}
        >
          {/* Zoom Controls Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1,
              py: 1,
              backgroundColor: "#fff",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Certificate Preview
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Zoom: {zoomLevel}%
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: 200,
                }}
              >
                <Tooltip title="Zoom Out">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setZoomLevel((prev) => Math.max(prev - 10, 50))
                    }
                  >
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Slider
                  value={zoomLevel}
                  onChange={handleZoomChange}
                  min={50}
                  max={200}
                  step={5}
                  size="small"
                  sx={{ flex: 1 }}
                />

                <Tooltip title="Zoom In">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setZoomLevel((prev) => Math.min(prev + 10, 200))
                    }
                  >
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Tooltip title="Use Ctrl + Mouse Wheel to zoom">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                >
                  Ctrl + Scroll
                </Typography>
              </Tooltip>
            </Box>
          </Box>

          {/* Preview Container */}
          <Box
            id="certificate-preview-container"
            sx={{
              flex: 1,
              overflow: "auto",
              p: 3,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              backgroundColor: "#e5e7eb",
            }}
          >
            {selectedStudents?.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  textAlign: "center",
                  backgroundColor: "#fff",
                  border: "2px dashed #e0e0e0",
                  borderRadius: 2,
                  mt: 8,
                }}
              >
                <Typography color="text.secondary" variant="h6" sx={{ mb: 1 }}>
                  No Students Selected
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Please select students from the table to preview certificates
                </Typography>
              </Paper>
            ) : (
              <Box
                ref={printRef}
                data-print-container
                sx={{
                  transform: `scale(${zoomLevel / 100})`,
                  transformOrigin: "top center",
                  transition: "transform 0.2s ease",
                  width: "100%",
                  maxWidth: pageOrientation === "landscape" ? "297mm" : "210mm",
                  margin: "0 auto",
                  backgroundColor: "#ffffff",
                  borderRadius: 2,
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {loading && (
                  <Box
                    className="loading-overlay"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1000,
                      borderRadius: 2,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
                <CertificateTemplate
                  selectedStudents={selectedStudents}
                  schoolDetail={schoolDetail}
                  selectedSession={selectedSession}
                  template={template}
                  certificatesPerPage={certificatesPerPage}
                  checkBoxEnabled={checkBoxEnabled}
                  certificateTitle={certificateTitle}
                  certificateSubtitle={certificateSubtitle}
                  courseName={courseName}
                  description={description}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  accentColor={accentColor}
                  fontFamily={fontFamily}
                  borderStyle={borderStyle}
                  borderWidth={borderWidth}
                  pageOrientation={pageOrientation}
                  fieldStyles={fieldStyles}
                />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <Divider />
      <DialogActions
        sx={{
          p: 2,
          gap: 2,
          justifyContent: "space-between",
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            onClick={handleResetTemplate}
            color="warning"
            variant="outlined"
            size="large"
            startIcon={<RestartAltIcon />}
            sx={{ textTransform: "capitalize" }}
          >
            Reset to Default
          </Button>
          {/* Only show Save Template button if it's not a default template (Classic, Modern, Elegant) */}
          {!["Classic", "Modern", "Elegant"].includes(selectedTemplateName) && (
            <Button
              onClick={handleSaveTemplate}
              color="success"
              variant="contained"
              size="large"
              startIcon={<SaveIcon />}
              disabled={saveTemplateMutation.isLoading}
              sx={{ textTransform: "capitalize", minWidth: "140px" }}
            >
              {saveTemplateMutation.isLoading ? "Saving..." : "Save Template"}
            </Button>
          )}
          <Button
            onClick={() => setShowSaveModal(true)}
            color="primary"
            variant="contained"
            size="large"
            startIcon={<SaveAsIcon />}
            sx={{ textTransform: "capitalize", minWidth: "160px" }}
          >
            Save as New Template
          </Button>
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            onClick={handleClose}
            color="primary"
            variant="outlined"
            size="large"
          >
            Cancel
          </Button>
          <Button
            size="large"
            variant="contained"
            color="secondary"
            onClick={handlePrintClick}
            disabled={loading || selectedStudents.length === 0}
            sx={{ textTransform: "capitalize", minWidth: "160px" }}
          >
            {loading ? "Preparing..." : "Print Certificates"}
          </Button>
        </Box>
      </DialogActions>

      {/* Save Template Modal */}
      <SaveTemplateModal
        open={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveAsNew}
        isLoading={saveAsNewMutation.isLoading}
      />
    </Dialog>
  );
};

export default PrintCertificateDialog;
